'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHeroSection } from '@/components/organisms/shared/PageHeroSection';
import styles from './Level2Layout.module.scss';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface ContentItem {
  id: string;
  title: string;
  description: string;
  href: string;
  icon?: string;
  tags?: string[];
  date?: string;
  image?: string; // 画像追加
  status?: 'active' | 'coming-soon' | 'new'; // ステータス追加
}

interface FeatureItem {
  title: string;
  description: string;
  icon?: string;
}

interface Level2LayoutProps {
  breadcrumbs: BreadcrumbItem[];
  title: string;
  description: string; // Made required
  items: ContentItem[];
  features?: {
    title: string;
    items: FeatureItem[];
  };
  ctaSection?: {
    title: string;
    description: string;
    buttonText: string;
    buttonHref: string;
    icon?: string;
  };
  emptyMessage?: string;
}

// Animation variants for Framer Motion
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring" as const,
      damping: 20,
      stiffness: 100,
    },
  },
};

// Removed unused headerVariants - now using UnifiedPageHeader

/**
 * Level 2 Layout Template
 * Standard layout for category hub pages (tier 2) with enhanced animations
 * Optimized performance with Framer Motion for smooth transitions
 */
const Level2Layout: React.FC<Level2LayoutProps> = ({
  breadcrumbs,
  title,
  description,
  items,
  features,
  ctaSection,
  emptyMessage,
}) => {
  const [_visibleCards, setVisibleCards] = useState<Set<string>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Intersection Observerでカードの表示アニメーション制御
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-id');
            if (id) {
              setVisibleCards((prev) => new Set(prev).add(id));
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    const cards = document.querySelectorAll(`.${styles.contentCard}`);
    cards.forEach((card) => {
      observerRef.current?.observe(card);
    });

    return () => {
      cards.forEach((card) => {
        observerRef.current?.unobserve(card);
      });
    };
  }, [items]);

  return (
    <div className={styles.level2Layout}>
      {/* Page Hero Section */}
      <PageHeroSection
        title={title}
        description={description}
        breadcrumbs={breadcrumbs.map(item => ({ label: item.label, href: item.href }))}
        level={2}
        hubStats={[
          { number: items.length, label: 'サービス', icon: '📋' },
          ...(features ? [{ number: features.items.length, label: '特徴', icon: '✨' }] : []),
          ...(items.filter(item => item.status === 'new').length > 0 ? 
            [{ number: items.filter(item => item.status === 'new').length, label: '新着', icon: '🆕' }] : [])
        ]}
      />

      {/* Main Content Grid */}
      <main className={styles.mainContent}>
        <div className={styles.container}>
          {items.length > 0 ? (
            <motion.div 
              className={styles.contentGrid}
              variants={containerVariants}
              initial="hidden"
              animate={isLoaded ? "visible" : "hidden"}
            >
              <AnimatePresence>
                {items.map((item, index) => (
                  <motion.article 
                    key={item.id} 
                    className={`${styles.contentCard} ${
                      item.status ? styles[`status-${item.status}`] : ''
                    }`}
                    data-id={item.id}
                    variants={itemVariants}
                    whileHover={{ 
                      y: -5, 
                      scale: 1.02,
                      transition: { duration: 0.2 }
                    }}
                    whileTap={{ scale: 0.98 }}
                    style={{ 
                      '--card-index': index,
                    } as React.CSSProperties}
                  >
                    <Link href={item.href} className={styles.cardLink}>
                    {item.image && (
                      <div className={styles.cardImage}>
                        <img src={item.image} alt="" loading="lazy" />
                      </div>
                    )}
                    <div className={styles.cardContent}>
                      {item.status && (
                        <span className={`${styles.statusBadge} ${styles[item.status]}`}>
                          {item.status === 'new' && '新着'}
                          {item.status === 'coming-soon' && '準備中'}
                          {item.status === 'active' && 'アクティブ'}
                        </span>
                      )}
                      {item.icon && (
                        <div className={styles.cardIcon}>{item.icon}</div>
                      )}
                      <h2 className={styles.cardTitle}>{item.title}</h2>
                      <p className={styles.cardDescription}>{item.description}</p>
                      {item.tags && item.tags.length > 0 && (
                        <div className={styles.cardTags}>
                          {item.tags.map((tag, index) => (
                            <span key={index} className={styles.tag}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {item.date && (
                        <time className={styles.cardDate} dateTime={item.date}>
                          {new Date(item.date).toLocaleDateString('ja-JP')}
                        </time>
                      )}
                      <span className={styles.cardArrow} aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 20 20">
                          <path d="M7 3l7 7-7 7" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </div>
                  </Link>
                  </motion.article>
                ))}
              </AnimatePresence>
            </motion.div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📋</div>
              <p className={styles.emptyMessage}>
                {emptyMessage || 'コンテンツは準備中です。'}
              </p>
              {ctaSection && (
                <Link href={ctaSection.buttonHref} className={styles.emptyCtaButton}>
                  {ctaSection.buttonText}
                </Link>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Optional Features Section - エンハンスド */}
      {features && (
        <section className={styles.featuresSection}>
          <div className={styles.container}>
            <h2 className={styles.featuresTitle}>
              <span className={styles.titleText}>{features.title}</span>
              <span className={styles.titleLine} aria-hidden="true" />
            </h2>
            <div className={styles.featuresGrid}>
              {features.items.map((feature, index) => (
                <div 
                  key={index} 
                  className={styles.featureItem}
                  style={{ '--feature-index': index } as React.CSSProperties}
                >
                  {feature.icon && (
                    <div className={styles.featureIcon}>
                      <span className={styles.iconInner}>{feature.icon}</span>
                      <span className={styles.iconBg} aria-hidden="true" />
                    </div>
                  )}
                  <h3 className={styles.featureTitle}>{feature.title}</h3>
                  <p className={styles.featureDescription}>
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - エンハンスド */}
      {ctaSection && (
        <motion.section 
          className={styles.ctaSection}
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className={styles.ctaBgPattern} aria-hidden="true">
            <div className={styles.ctaCircle1} />
            <div className={styles.ctaCircle2} />
            <div className={styles.ctaCircle3} />
          </div>
        <motion.div 
          className={styles.ctaContent}
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.div className={styles.ctaIconWrapper}>
            <div className={styles.ctaIcon}>
              {ctaSection.icon || '🚀'}
            </div>
            <div className={styles.ctaIconPulse} />
          </motion.div>
          <motion.h2 
            className={styles.ctaTitle}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {ctaSection.title}
          </motion.h2>
          <motion.p 
            className={styles.ctaDescription}
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {ctaSection.description}
          </motion.p>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Link href={ctaSection.buttonHref} className={styles.ctaButton}>
              <span className={styles.ctaButtonBg} />
              <span className={styles.ctaButtonContent}>
                <span className={styles.ctaButtonText}>{ctaSection.buttonText}</span>
                <span className={styles.ctaButtonIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </span>
            </Link>
          </motion.div>
        </motion.div>
        </motion.section>
      )}
    </div>
  );
};

export default Level2Layout;